import { useState, useContext, useEffect } from 'react'
import { enqueueSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import Markdown from 'react-markdown'
import rehypeExternalLinks from 'rehype-external-links'
import _ from 'lodash'

import { Button, Typography, TextField, Box } from '@mui/material'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'

import { AppContext } from '/providers/app-context'
import { createTemplate } from '/providers/templates-api'
import PopupShell from '../editor/popup-shell'
import { ApplePreviewImage } from '/components/templates/apple-preview-mask.jsx'
import { setCurrentTemplateId } from '/utils/current-template'

const templatePreviewImageUrl = (template, platform = "apple") => {
  const folder = platform === "google" ? "template_previews_google" : "template_previews"
  const cacheBuster = template?.last_modified_date ?? template?.last_modified ?? ""
  const ownerClientId = template?.client_id ?? 0
  return `https://s3.eu-west-1.amazonaws.com/images.smartix.uk/${ownerClientId}/${folder}/${template.template_id}.png${cacheBuster ? `?v=${encodeURIComponent(cacheBuster)}` : ""}`
}

const SelectedTemplateDialog = ({ open, setOpen, template, onCreated, inline = false }) => {

  const { apiSource, apiKey, refreshTemplates, processing, setProcessing } = useContext(AppContext)
  const navigate = useNavigate()

  const [selectedTemplate, setSelectedTemplate] = useState()
  const [templateName, setTemplateName] = useState("")

  useEffect(() => {
    if (template) setSelectedTemplate(_.cloneDeep(template))
  }, [template])

  const doCreateTemplate = async () => {
    close()
    setProcessing(true)
    enqueueSnackbar({ message: 'Creating template', variant: 'light' })
    const newTemplate = await createTemplate({ apiSource, apiKey, templateId: template.template_id, templateName })
    setProcessing(false)
    if (!newTemplate.error) {
      const newTemplateId = newTemplate.template.template_id
      setCurrentTemplateId(newTemplateId)
      enqueueSnackbar({ message: 'Template created', variant: 'success' })
      await refreshTemplates({ force: true })
      if (onCreated) {
        onCreated(newTemplateId)
      } else {
        navigate(`/templates/${newTemplateId}/edit`, { replace: true })
      }
    } else {
      enqueueSnackbar({ message: 'Template creation failed: ' + newTemplate.error, variant: 'error' })
    }
  }

  const close = (_e, _reason) => {
    //    if (reason && reason === "backdropClick")
    //      return
    setOpen(false)
  }

  if (selectedTemplate) {
    const content = (
      <>
      <div className="flex flex-row justify-between">
        <Typography variant="h6">Create New Template : {selectedTemplate.name}</Typography>
      </div>

      <div className='overflow-y-auto'>

        <p className='mb-5'>Once your template has been created, you can use the editor to customise your template.</p>
        <p className='mb-5 font-semibold'>Please choose a name for your new template</p>

        <TextField value={templateName}
          onChange={e => setTemplateName(e.target.value.substring(0, 40))}
          label="New template Name (minimum length 5)"
          slotProps={{ inputLabel: { shrink: true } }}
          type="search"
          sx={{ width: "100%" }}
          disabled={template?.specialist_template === 1}
        />


        <div className="flex flex-row justify-end w-full p-5">
          <Button variant="contained" onClick={doCreateTemplate} autoFocus disabled={processing || templateName.length < 5 || template?.specialist_template === 1}><ArchiveOutlinedIcon className="mr-5" />Create New Template</Button>
        </div>

        <hr className='mb-5' />

        <div className='flex flex-col gap-10 mt-10'>

          <div className='flex flex-row gap-5 '>
            <div className='flex flex-col items-center'>
              <Typography variant="h6">Apple sample preview</Typography>
              <ApplePreviewImage
                template={selectedTemplate}
                src={templatePreviewImageUrl(selectedTemplate, "apple")}
                alt={`${selectedTemplate.name} Apple sample preview`}
                style={{ width: 375, minWidth: 375, maxWidth: 375 }}
              />
            </div>

            <div className='flex flex-col items-center'>
              <Typography variant="h6">{selectedTemplate.apple_skeleton} pass type editable areas</Typography>
              <img src={`https://s3.eu-west-1.amazonaws.com/images.smartix.uk/previews/${selectedTemplate.apple_skeleton}-apple-skeleton.png`} style={{ width: 375, minWidth:375, maxWidth:375 }} />
            </div>
          </div>

          <div className='flex flex-row gap-5'>

            <div className='flex flex-col items-center'>
              <Typography variant="h6">Google sample preview</Typography>
              <img src={templatePreviewImageUrl(selectedTemplate, "google")} style={{ width: 375, minWidth:375, maxWidth:375 }} />
            </div>

            <div className='flex flex-col items-center'>
              <Typography variant="h6">{selectedTemplate.google_pass_type} pass type editable areas</Typography>
              <img src={`https://s3.eu-west-1.amazonaws.com/images.smartix.uk/previews/${selectedTemplate.google_pass_type}-google-skeleton.png`} style={{ width: 375, minWidth:375, maxWidth:375 }} />
            </div>
          </div>

          <hr className='mb-5' />

            <div className='mb-5'><Markdown rehypePlugins={[[rehypeExternalLinks, {
              properties: {
                className: ['link'],
              },
              target: '_blank'
            }]]}>{selectedTemplate.long_description}</Markdown></div>

        </div>

      </div>
      </>
    )

    return inline
      ? <Box sx={{ height: '100%', p: 3, overflow: 'auto' }}>{content}</Box>
      : <PopupShell open={open} onClose={close} width={830} variant="solid">{content}</PopupShell>
  }

  return null
}

export default SelectedTemplateDialog
